import { createTheme, alpha } from "@mui/material/styles";

// ─── Custom theme token types ────────────────────────────────────────────────
declare module "@mui/material/styles" {
    interface Theme {
        customTokens: {
            status: {
                incomplete: string;
                pending: string;
                candidate: string;
                placed: string;
                rejected: string;
                blocked: string;
                available: string;
                unavailable: string;
            };
        };
        // Keep old interface for backward compatibility (no-ops)
        iconTheme: { main: string; hover: string };
        buttonTheme: { main: string; hover: string };
    }
    interface ThemeOptions {
        customTokens?: {
            status?: {
                incomplete?: string;
                pending?: string;
                candidate?: string;
                placed?: string;
                rejected?: string;
                blocked?: string;
                available?: string;
                unavailable?: string;
            };
        };
        iconTheme?: { main: string; hover: string };
        buttonTheme?: { main: string; hover: string };
    }
}

// ─── Shared values ───────────────────────────────────────────────────────────
const fontFamily = '"Inter Variable", "Inter", system-ui, -apple-system, sans-serif';

const shape = { borderRadius: 12 };

const typography = {
    fontFamily,
    h1: { fontSize: '2.25rem', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.025em' },
    h2: { fontSize: '1.875rem', fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.025em' },
    h3: { fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.3 },
    h4: { fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.35 },
    h5: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
    body1: { fontSize: '0.875rem', lineHeight: 1.6 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
    button: { fontSize: '0.8125rem', fontWeight: 500, textTransform: 'none' as const },
    caption: { fontSize: '0.75rem', lineHeight: 1.5 },
    overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' as const },
};

// ─── Light palette ───────────────────────────────────────────────────────────
const lightPalette = {
    mode: 'light' as const,
    primary:    { main: '#2563EB', dark: '#1D4ED8', light: '#60A5FA' },
    secondary:  { main: '#6366F1', dark: '#4F46E5', light: '#818CF8' },
    success:    { main: '#16A34A', dark: '#15803D', light: '#4ADE80' },
    warning:    { main: '#CA8A04', dark: '#A16207', light: '#FACC15' },
    error:      { main: '#DC2626', dark: '#B91C1C', light: '#F87171' },
    info:       { main: '#0EA5E9', dark: '#0284C7', light: '#38BDF8' },
    background: { default: '#F8FAFC', paper: '#FFFFFF' },
    text:       { primary: '#0F172A', secondary: '#64748B' },
    divider:    '#E2E8F0',
    action: {
        hover: 'rgba(15, 23, 42, 0.04)',
        selected: 'rgba(15, 23, 42, 0.08)',
        disabled: 'rgba(15, 23, 42, 0.26)',
        disabledBackground: 'rgba(15, 23, 42, 0.12)',
    },
};

// ─── Dark palette ────────────────────────────────────────────────────────────
const darkPalette = {
    mode: 'dark' as const,
    primary:    { main: '#60A5FA', dark: '#3B82F6', light: '#93C5FD' },
    secondary:  { main: '#818CF8', dark: '#6366F1', light: '#A5B4FC' },
    success:    { main: '#4ADE80', dark: '#22C55E', light: '#86EFAC' },
    warning:    { main: '#FACC15', dark: '#EAB308', light: '#FDE047' },
    error:      { main: '#F87171', dark: '#EF4444', light: '#FCA5A5' },
    info:       { main: '#38BDF8', dark: '#0EA5E9', light: '#7DD3FC' },
    background: { default: '#0B0F1A', paper: '#111827' },
    text:       { primary: '#F1F5F9', secondary: '#94A3B8' },
    divider:    '#1E293B',
    action: {
        hover: 'rgba(241, 245, 249, 0.06)',
        selected: 'rgba(241, 245, 249, 0.1)',
        disabled: 'rgba(241, 245, 249, 0.3)',
        disabledBackground: 'rgba(241, 245, 249, 0.12)',
    },
};

// ─── Status tokens ───────────────────────────────────────────────────────────
const lightStatusTokens = {
    incomplete: '#DC2626',
    pending:    '#CA8A04',
    candidate:  '#16A34A',
    placed:     '#0EA5E9',
    rejected:   '#DB2777',
    blocked:    '#78716C',
    available:  '#16A34A',
    unavailable:'#DC2626',
};

const darkStatusTokens = {
    incomplete: '#F87171',
    pending:    '#FACC15',
    candidate:  '#4ADE80',
    placed:     '#38BDF8',
    rejected:   '#F472B6',
    blocked:    '#A8A29E',
    available:  '#4ADE80',
    unavailable:'#F87171',
};

// ─── Shared component overrides (mode-agnostic) ─────────────────────────────
function buildOverrides(mode: 'light' | 'dark') {
    const palette = mode === 'light' ? lightPalette : darkPalette;
    const divider = palette.divider;
    const bgDefault = palette.background.default;
    const primary = palette.primary.main;

    return {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    fontFamily,
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    textRendering: 'optimizeLegibility',
                },
                '::selection': {
                    backgroundColor: alpha(primary, 0.2),
                    color: palette.text.primary,
                },
            },
        },
        MuiButtonBase: {
            defaultProps: { disableRipple: true },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    textTransform: 'none' as const,
                    fontWeight: 500,
                    boxShadow: 'none',
                    transition: 'background-color 150ms ease, border-color 150ms ease, color 150ms ease',
                    '&:hover': { boxShadow: 'none' },
                },
                sizeSmall: { fontSize: '0.75rem', padding: '4px 10px' },
                sizeMedium: { fontSize: '0.8125rem', padding: '6px 16px' },
                sizeLarge: { fontSize: '0.875rem', padding: '10px 22px' },
                containedPrimary: {
                    '&:hover': { backgroundColor: palette.primary.dark },
                },
            },
        },
        MuiPaper: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    border: `1px solid ${divider}`,
                    backgroundImage: 'none',
                    boxShadow: mode === 'light'
                        ? '0 1px 3px 0 rgba(0,0,0,0.04), 0 1px 2px -1px rgba(0,0,0,0.04)'
                        : '0 1px 3px 0 rgba(0,0,0,0.3), 0 1px 2px -1px rgba(0,0,0,0.2)',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    border: `1px solid ${divider}`,
                    boxShadow: mode === 'light'
                        ? '0 1px 3px 0 rgba(0,0,0,0.04)'
                        : '0 1px 3px 0 rgba(0,0,0,0.3)',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    fontWeight: 500,
                    fontSize: '0.75rem',
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: divider,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha(primary, 0.5),
                    },
                },
            },
        },
        MuiTextField: {
            defaultProps: { size: 'small' as const },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                    border: `1px solid ${divider}`,
                    boxShadow: mode === 'light'
                        ? '0 20px 60px -12px rgba(0,0,0,0.15)'
                        : '0 20px 60px -12px rgba(0,0,0,0.5)',
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: { padding: '20px 24px 12px', fontWeight: 600 },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: { padding: '8px 24px 16px' },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: { padding: '12px 24px 20px' },
            },
        },
        MuiAppBar: {
            defaultProps: { elevation: 0 },
            styleOverrides: {
                root: {
                    backgroundColor: bgDefault,
                    color: palette.text.primary,
                    borderBottom: `1px solid ${divider}`,
                    boxShadow: mode === 'light'
                        ? '0 1px 3px rgba(0,0,0,0.05)'
                        : '0 1px 3px rgba(0,0,0,0.3)',
                },
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    borderColor: divider,
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    height: 2,
                    borderRadius: 1,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none' as const,
                    fontWeight: 500,
                    minHeight: 40,
                    fontSize: '0.875rem',
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    '& .MuiTableCell-head': {
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase' as const,
                        letterSpacing: '0.05em',
                        color: palette.text.secondary,
                        backgroundColor: bgDefault,
                        borderBottom: `2px solid ${divider}`,
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    padding: '12px 16px',
                    borderBottom: `1px solid ${divider}`,
                    fontSize: '0.8125rem',
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:nth-of-type(even)': {
                        backgroundColor: mode === 'light'
                            ? 'rgba(0,0,0,0.02)'
                            : 'rgba(255,255,255,0.02)',
                    },
                    '&:hover': {
                        backgroundColor: alpha(primary, 0.06),
                    },
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: { borderRadius: 8 },
                standardSuccess: { backgroundColor: alpha(palette.success.main, 0.1) },
                standardError: { backgroundColor: alpha(palette.error.main, 0.1) },
                standardWarning: { backgroundColor: alpha(palette.warning.main, 0.1) },
                standardInfo: { backgroundColor: alpha(palette.info.main, 0.1) },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    borderRadius: 6,
                    fontSize: '0.75rem',
                    padding: '6px 12px',
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    borderRadius: 10,
                    border: `1px solid ${divider}`,
                    boxShadow: mode === 'light'
                        ? '0 4px 20px rgba(0,0,0,0.08)'
                        : '0 4px 20px rgba(0,0,0,0.4)',
                },
            },
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    borderRadius: 10,
                    border: `1px solid ${divider}`,
                    boxShadow: mode === 'light'
                        ? '0 4px 20px rgba(0,0,0,0.08)'
                        : '0 4px 20px rgba(0,0,0,0.4)',
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    margin: '2px 6px',
                    padding: '8px 12px',
                    fontSize: '0.8125rem',
                    '&:hover': {
                        backgroundColor: alpha(primary, 0.06),
                    },
                    '&.Mui-selected': {
                        backgroundColor: alpha(primary, 0.1),
                        '&:hover': {
                            backgroundColor: alpha(primary, 0.14),
                        },
                    },
                },
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: { fontWeight: 600 },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: { borderRadius: 8 },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    '&.Mui-selected': {
                        backgroundColor: alpha(primary, 0.08),
                        '&:hover': {
                            backgroundColor: alpha(primary, 0.12),
                        },
                    },
                },
            },
        },
    };
}

// ─── Theme factory ───────────────────────────────────────────────────────────
export const lightTheme = createTheme({
    palette: lightPalette,
    typography,
    shape,
    customTokens: { status: lightStatusTokens },
    iconTheme:   { main: '#2563EB', hover: '#1D4ED8' },
    buttonTheme: { main: '#2563EB', hover: '#1D4ED8' },
    components: buildOverrides('light'),
});

export const darkTheme = createTheme({
    palette: darkPalette,
    typography,
    shape,
    customTokens: { status: darkStatusTokens },
    iconTheme:   { main: '#60A5FA', hover: '#3B82F6' },
    buttonTheme: { main: '#60A5FA', hover: '#3B82F6' },
    components: buildOverrides('dark'),
});

// Default export for backward compatibility
const theme = lightTheme;
export default theme;
